// src/pages/cliente/Settings.jsx

import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { post, del, get } from '../../helpers/apiHelper';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faLock,
  faBell,
  faEnvelope,
  faTelegram,
  faCog,
  faTrash,
  faCreditCard,
  faShield,
  faPalette,
  faLanguage,
  faGlobe,
  faChevronRight,
  faCheck,
  faTimes,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram as faTelegramBrand } from '@fortawesome/free-brands-svg-icons';

function Settings() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [currentPlan, setCurrentPlan] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('profile');
  const [notificationConfig, setNotificationConfig] = useState({
    email: { enabled: false, smtpHost: '', smtpPort: '', smtpUser: '', smtpPass: '', fromEmail: '' },
    telegram: { enabled: false, botToken: '', chatId: '', botUsername: '' },
    push: { enabled: false, vapidKey: '', firebaseConfig: {} }
  });
  const [marcas, setMarcas] = useState([]);
  const [selectedMarca, setSelectedMarca] = useState('');

  const navigate = useNavigate();
  const { logout, user, trabajador, isAuthenticated } = useAuthStore();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user && user.userId) {
          const response = await get(`users/${user.userId}`);
          if (response) {
            setUserData(response);
            setCurrentPlan(response.plan);
          }

          // Obtener marcas del usuario
          const marcasResponse = await get(`marcas/user/${user.userId}`);
          setMarcas(marcasResponse);
          if (marcasResponse.length > 0) {
            setSelectedMarca(marcasResponse[0]._id);
            // Cargar configuración de notificaciones para la primera marca
            await loadNotificationConfig(marcasResponse[0]._id);
          }
        } else {
          throw new Error('User ID is undefined');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        Swal.fire('Error', 'No se pudo obtener los datos del usuario.', 'error').then(() => {
          navigate('/login');
        });
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchUserData();
    } else {
      setLoading(false);
      Swal.fire('Error', 'Usuario no autenticado.', 'error').then(() => {
        navigate('/login');
      });
    }
  }, [user, navigate, isAuthenticated]);

  const loadNotificationConfig = async (marcaId) => {
    try {
      // Intentar cargar configuración del backend
      const config = await get(`notification-config/${marcaId}`);
      if (config) {
        setNotificationConfig(config);
      }
    } catch (error) {
      console.log('No se encontró configuración de notificaciones en el backend. Usando configuración local.');

      // Si no existe el endpoint, intentar cargar desde localStorage
      const localConfig = localStorage.getItem(`notification-config-${marcaId}`);
      if (localConfig) {
        try {
          setNotificationConfig(JSON.parse(localConfig));
        } catch (parseError) {
          console.log('Error parseando configuración local. Usando configuración por defecto.');
          setNotificationConfig({
            email: { enabled: false, smtpHost: '', smtpPort: '', smtpUser: '', smtpPass: '', fromEmail: '' },
            telegram: { enabled: false, botToken: '', chatId: '', botUsername: '' },
            push: { enabled: false, vapidKey: '', firebaseConfig: {} }
          });
        }
      } else {
        // Usar configuración por defecto
        setNotificationConfig({
          email: { enabled: false, smtpHost: '', smtpPort: '', smtpUser: '', smtpPass: '', fromEmail: '' },
          telegram: { enabled: false, botToken: '', chatId: '', botUsername: '' },
          push: { enabled: false, vapidKey: '', firebaseConfig: {} }
        });
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      Swal.fire('Error', 'Todos los campos de contraseña son obligatorios.', 'error');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      Swal.fire('Error', 'Las contraseñas nuevas no coinciden.', 'error');
      return;
    }

    Swal.fire({
      title: '¿Estás seguro?',
      text: "¿Deseas cambiar tu contraseña?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, cambiarla',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await post('auth/change-password', { oldPassword, newPassword });
          Swal.fire('¡Actualizado!', 'Tu contraseña ha sido cambiada.', 'success').then(() => {
            navigate('/dashboard');
          });
        } catch (error) {
          Swal.fire('Error', error.message, 'error');
        }
      }
    });
  };

  const handleDeleteAccount = async () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¿Realmente deseas eliminar tu cuenta? Este proceso no puede deshacerse.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminarla',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await del('auth/delete-account');
          logout();
          Swal.fire('¡Eliminado!', 'Tu cuenta ha sido eliminada.', 'success').then(() => {
            navigate('/');
          });
        } catch (error) {
          Swal.fire('Error', error.message, 'error');
        }
      }
    });
  };

  const handleNavigateToSubscribe = () => {
    navigate('/subscribe');
  };

  const handleNotificationConfigChange = (channel, field, value) => {
    setNotificationConfig(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [field]: value
      }
    }));
  };

  const handleSaveNotificationConfig = async () => {
    try {
      // Intentar guardar en el backend
      await post(`notification-config/${selectedMarca}`, notificationConfig);
      Swal.fire('¡Guardado!', 'Configuración de notificaciones actualizada correctamente.', 'success');
    } catch (error) {
      console.error('Error guardando configuración:', error);

      // Manejar diferentes tipos de errores
      if (error.message?.includes('404')) {
        // Si el endpoint no existe, guardar localmente como fallback
        console.log('Endpoint de configuración no disponible. Guardando localmente.');
        localStorage.setItem(`notification-config-${selectedMarca}`, JSON.stringify(notificationConfig));
        Swal.fire('¡Guardado!', 'Configuración guardada localmente. Los endpoints del backend no están disponibles.', 'success');
      } else if (error.message?.includes('500')) {
        Swal.fire('Error del servidor', 'Error interno del servidor al guardar la configuración.', 'error');
      } else if (error.message?.includes('401') || error.message?.includes('403')) {
        Swal.fire('Error de autenticación', 'No tienes permisos para realizar esta acción.', 'error');
      } else {
        Swal.fire('Error de conexión', 'No se pudo guardar la configuración. Verifica tu conexión.', 'error');
      }
    }
  };

  const testNotification = async (channel) => {
    try {
      if (channel === 'push') {
        // Verificar si Firebase está disponible para notificaciones push
        const { isFirebaseAvailable } = await import('../../firebase/firebase-config');
        if (!isFirebaseAvailable()) {
          Swal.fire('Error', 'Firebase no está configurado para notificaciones push. Configura las variables de entorno primero.', 'error');
          return;
        }
      }

      // Verificar que el canal esté habilitado
      if (!notificationConfig[channel]?.enabled) {
        Swal.fire('Error', `Las notificaciones de ${channel} no están habilitadas. Habilita el canal primero.`, 'error');
        return;
      }

      // Verificar que haya configuración válida
      const config = notificationConfig[channel];
      let isValid = false;

      switch (channel) {
        case 'email':
          isValid = config.smtpHost && config.smtpUser && config.smtpPass && config.fromEmail;
          break;
        case 'telegram':
          isValid = config.botToken && config.chatId;
          break;
        case 'push':
          isValid = config.vapidKey && config.firebaseConfig?.projectId;
          break;
      }

      if (!isValid) {
        Swal.fire('Error', `Configuración de ${channel} incompleta. Completa todos los campos requeridos.`, 'error');
        return;
      }

      // Intentar enviar notificación de prueba
      const response = await post(`notifications/test-${channel}`, {
        marcaId: selectedMarca,
        config: notificationConfig
      });

      Swal.fire('¡Enviado!', `Notificación de prueba enviada por ${channel} correctamente.`, 'success');
    } catch (error) {
      console.error(`Error probando notificación ${channel}:`, error);

      // Manejar diferentes tipos de errores
      if (error.message?.includes('404')) {
        Swal.fire(
          'Endpoint no encontrado',
          `El endpoint de prueba para ${channel} no está disponible en el servidor. Contacta al administrador.`,
          'error'
        );
      } else if (error.message?.includes('500')) {
        Swal.fire(
          'Error del servidor',
          `Error interno del servidor al probar ${channel}. Verifica la configuración.`,
          'error'
        );
      } else if (error.message?.includes('401') || error.message?.includes('403')) {
        Swal.fire(
          'Error de autenticación',
          'No tienes permisos para realizar esta acción.',
          'error'
        );
      } else {
        Swal.fire(
          'Error de conexión',
          `No se pudo enviar la notificación de prueba por ${channel}. Verifica tu conexión y la configuración.`,
          'error'
        );
      }
    }
  };

  const renderSectionButton = (id, icon, title, description, badge = null) => (
    <button
      onClick={() => setActiveSection(id)}
      className={`w-full p-4 rounded-lg border transition-all duration-200 ${activeSection === id
        ? 'border-blue-500 bg-blue-50 shadow-md'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
        }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${activeSection === id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
            <FontAwesomeIcon icon={icon} className="w-5 h-5" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {badge && (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              {badge}
            </span>
          )}
          <FontAwesomeIcon
            icon={faChevronRight}
            className={`w-4 h-4 transition-transform ${activeSection === id ? 'rotate-90' : ''
              }`}
          />
        </div>
      </div>
    </button>
  );

  const renderProfileSection = () => (
    <div className="space-y-6">
      {/* Información del Usuario */}
      {userData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <FontAwesomeIcon icon={faUser} className="w-5 h-5 mr-2 text-blue-500" />
            Información del Usuario
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <p className="text-gray-900 font-medium">{userData.nombre}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
              <p className="text-gray-900 font-medium">{userData.apellido}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico</label>
              <p className="text-gray-900 font-medium">{userData.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">País</label>
              <p className="text-gray-900 font-medium">{userData.pais}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <p className="text-gray-900 font-medium">{trabajador ? 'TRABAJADOR_ROLE' : 'USER_ROLE'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Plan y Suscripción */}
      {!trabajador && userData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <FontAwesomeIcon icon={faCreditCard} className="w-5 h-5 mr-2 text-green-500" />
            Plan Actual
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-900 font-medium">
                Plan {userData.plan.charAt(0).toUpperCase() + userData.plan.slice(1)}
              </p>
              <p className="text-sm text-gray-500">Gestiona tu suscripción</p>
            </div>
            <button
              onClick={handleNavigateToSubscribe}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              Cambiar Plan
            </button>
          </div>
        </div>
      )}

      {/* Cambiar Contraseña */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <FontAwesomeIcon icon={faLock} className="w-5 h-5 mr-2 text-red-500" />
          Cambiar Contraseña
        </h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña Actual
            </label>
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="********"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nueva Contraseña
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="********"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Nueva Contraseña
            </label>
            <input
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="********"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors"
          >
            Cambiar Contraseña
          </button>
        </form>
      </div>

      {/* Eliminar Cuenta */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <FontAwesomeIcon icon={faTrash} className="w-5 h-5 mr-2 text-red-500" />
          Eliminar Cuenta
        </h2>
        <p className="text-gray-600 mb-4">
          Esta acción no se puede deshacer. Se eliminarán todos tus datos permanentemente.
        </p>
        <button
          onClick={handleDeleteAccount}
          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
        >
          Eliminar Cuenta
        </button>
      </div>
    </div>
  );

  const renderNotificationSection = () => (
    <div className="space-y-6">
      {/* Ayuda General */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center text-blue-800">
          <FontAwesomeIcon icon={faBell} className="w-5 h-5 mr-2" />
          Sistema de Notificaciones
        </h2>
        <div className="text-sm text-blue-700 space-y-3">
          <p>
            <strong>¿Qué son las notificaciones?</strong> Te permiten enviar mensajes automáticos a tus clientes
            cuando ocurren eventos importantes como acumulación de puntos, canjes de premios, cumpleaños, etc.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-white p-3 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-1">📧 Email</h4>
              <p className="text-xs text-blue-600">Notificaciones por correo electrónico usando tu servidor SMTP</p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-1">📱 Telegram</h4>
              <p className="text-xs text-blue-600">Mensajes instantáneos a tu canal o grupo de Telegram</p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-1">🔔 Push</h4>
              <p className="text-xs text-blue-600">Notificaciones en el navegador de tus clientes</p>
            </div>
          </div>
          <p className="text-xs mt-3 p-2 bg-blue-100 rounded">
            💡 <strong>Consejo:</strong> Puedes habilitar múltiples canales para mayor cobertura.
            Las notificaciones se enviarán automáticamente según los eventos configurados en tu estrategia de fidelización.
          </p>
        </div>
      </div>

      {/* Selector de Marca */}
      {marcas.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <FontAwesomeIcon icon={faGlobe} className="w-5 h-5 mr-2 text-blue-500" />
            Seleccionar Marca
          </h2>
          <select
            value={selectedMarca}
            onChange={(e) => {
              setSelectedMarca(e.target.value);
              loadNotificationConfig(e.target.value);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {marcas.map((marca) => (
              <option key={marca._id} value={marca._id}>
                {marca.nombre}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Cada marca puede tener su propia configuración de notificaciones</p>
        </div>
      )}

      {/* Configuración Email */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <FontAwesomeIcon icon={faEnvelope} className="w-5 h-5 mr-2 text-blue-500" />
            Notificaciones por Email
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => testNotification('email')}
              className="px-3 py-1 text-sm bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
            >
              Probar
            </button>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationConfig.email.enabled}
                onChange={(e) => handleNotificationConfigChange('email', 'enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Explicación Email */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4 mr-2" />
            ¿Cómo configurar Email SMTP?
          </h3>
          <div className="text-sm text-blue-700 space-y-2">
            <p><strong>1. Gmail:</strong> Usa "smtp.gmail.com", puerto 587, y una contraseña de aplicación</p>
            <p><strong>2. Outlook/Hotmail:</strong> Usa "smtp-mail.outlook.com", puerto 587</p>
            <p><strong>3. Yahoo:</strong> Usa "smtp.mail.yahoo.com", puerto 587</p>
            <p><strong>4. Servidor propio:</strong> Consulta con tu proveedor de hosting</p>
            <p className="text-xs mt-2">
              💡 <strong>Consejo:</strong> Para Gmail, necesitas generar una "Contraseña de aplicación" en la configuración de seguridad de tu cuenta.
            </p>
          </div>
        </div>

        {notificationConfig.email.enabled && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Servidor SMTP *</label>
                <input
                  type="text"
                  value={notificationConfig.email.smtpHost}
                  onChange={(e) => handleNotificationConfigChange('email', 'smtpHost', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="smtp.gmail.com"
                />
                <p className="text-xs text-gray-500 mt-1">Ej: smtp.gmail.com, smtp-mail.outlook.com</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Puerto *</label>
                <input
                  type="number"
                  value={notificationConfig.email.smtpPort}
                  onChange={(e) => handleNotificationConfigChange('email', 'smtpPort', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="587"
                />
                <p className="text-xs text-gray-500 mt-1">587 (TLS) o 465 (SSL)</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usuario *</label>
                <input
                  type="text"
                  value={notificationConfig.email.smtpUser}
                  onChange={(e) => handleNotificationConfigChange('email', 'smtpUser', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="tu@email.com"
                />
                <p className="text-xs text-gray-500 mt-1">Tu dirección de email completa</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                <input
                  type="password"
                  value={notificationConfig.email.smtpPass}
                  onChange={(e) => handleNotificationConfigChange('email', 'smtpPass', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="********"
                />
                <p className="text-xs text-gray-500 mt-1">Contraseña de aplicación para Gmail</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Remitente *</label>
              <input
                type="email"
                value={notificationConfig.email.fromEmail}
                onChange={(e) => handleNotificationConfigChange('email', 'fromEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="noreply@tuempresa.com"
              />
              <p className="text-xs text-gray-500 mt-1">Email desde el cual se enviarán las notificaciones</p>
            </div>
          </div>
        )}
      </div>

      {/* Configuración Telegram */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <FontAwesomeIcon icon={faTelegramBrand} className="w-5 h-5 mr-2 text-blue-500" />
            Notificaciones por Telegram
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => testNotification('telegram')}
              className="px-3 py-1 text-sm bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
            >
              Probar
            </button>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationConfig.telegram.enabled}
                onChange={(e) => handleNotificationConfigChange('telegram', 'enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Explicación Telegram */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4 mr-2" />
            ¿Cómo configurar Telegram?
          </h3>
          <div className="text-sm text-blue-700 space-y-2">
            <p><strong>Paso 1:</strong> Crea un bot con @BotFather en Telegram</p>
            <p><strong>Paso 2:</strong> Obtén el token del bot (formato: 123456789:ABCdefGHIjklMNOpqrsTUVwxyz)</p>
            <p><strong>Paso 3:</strong> Obtén el Chat ID del canal/grupo donde quieres recibir notificaciones</p>
            <p className="text-xs mt-2">
              💡 <strong>Consejo:</strong> Para obtener el Chat ID, envía un mensaje al bot y visita: https://api.telegram.org/bot[TU_TOKEN]/getUpdates
            </p>
          </div>
        </div>

        {notificationConfig.telegram.enabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Token del Bot *</label>
              <input
                type="text"
                value={notificationConfig.telegram.botToken}
                onChange={(e) => handleNotificationConfigChange('telegram', 'botToken', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
              />
              <p className="text-xs text-gray-500 mt-1">Token obtenido de @BotFather</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chat ID *</label>
              <input
                type="text"
                value={notificationConfig.telegram.chatId}
                onChange={(e) => handleNotificationConfigChange('telegram', 'chatId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="-1001234567890"
              />
              <p className="text-xs text-gray-500 mt-1">ID del canal/grupo (empieza con -100 para canales)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username del Bot</label>
              <input
                type="text"
                value={notificationConfig.telegram.botUsername}
                onChange={(e) => handleNotificationConfigChange('telegram', 'botUsername', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="@tu_bot"
              />
              <p className="text-xs text-gray-500 mt-1">Opcional: username del bot (sin @)</p>
            </div>
          </div>
        )}
      </div>

      {/* Configuración Push */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <FontAwesomeIcon icon={faBell} className="w-5 h-5 mr-2 text-blue-500" />
            Notificaciones Push
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => testNotification('push')}
              className="px-3 py-1 text-sm bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
            >
              Probar
            </button>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notificationConfig.push.enabled}
                onChange={(e) => handleNotificationConfigChange('push', 'enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Explicación Push */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4 mr-2" />
            ¿Cómo configurar Push Notifications?
          </h3>
          <div className="text-sm text-blue-700 space-y-2">
            <p><strong>Paso 1:</strong> Crea un proyecto en <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Firebase Console</a></p>
            <p><strong>Paso 2:</strong> Habilita Cloud Messaging y genera la clave VAPID</p>
            <p><strong>Paso 3:</strong> Copia la configuración de Firebase y la clave VAPID</p>
            <p className="text-xs mt-2">
              💡 <strong>Consejo:</strong> Las notificaciones push aparecerán en el navegador de tus clientes, incluso cuando no estén en tu sitio web.
            </p>
          </div>
        </div>

        {notificationConfig.push.enabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clave VAPID *</label>
              <input
                type="text"
                value={notificationConfig.push.vapidKey}
                onChange={(e) => handleNotificationConfigChange('push', 'vapidKey', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="BL-your_vapid_key_here"
              />
              <p className="text-xs text-gray-500 mt-1">Clave VAPID de Firebase Cloud Messaging</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Configuración Firebase (JSON) *</label>
              <textarea
                value={JSON.stringify(notificationConfig.push.firebaseConfig, null, 2)}
                onChange={(e) => {
                  try {
                    const config = JSON.parse(e.target.value);
                    handleNotificationConfigChange('push', 'firebaseConfig', config);
                  } catch (error) {
                    // Ignorar errores de JSON inválido
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="6"
                placeholder='{"apiKey": "your_api_key", "authDomain": "your_project.firebaseapp.com", "projectId": "your_project_id", "storageBucket": "your_project.appspot.com", "messagingSenderId": "123456789", "appId": "1:123456789:web:abcdef"}'
              />
              <p className="text-xs text-gray-500 mt-1">Configuración completa de Firebase en formato JSON</p>
            </div>
          </div>
        )}
      </div>

      {/* Botón Guardar */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveNotificationConfig}
          className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors"
        >
          Guardar Configuración
        </button>
      </div>

      {/* Consejos y Mejores Prácticas */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center text-yellow-800">
          <FontAwesomeIcon icon={faInfoCircle} className="w-5 h-5 mr-2" />
          Consejos y Mejores Prácticas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-yellow-800 mb-2">🔒 Seguridad</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Nunca compartas tokens o contraseñas</li>
              <li>• Usa contraseñas de aplicación para Gmail</li>
              <li>• Mantén actualizadas las credenciales</li>
              <li>• Revisa regularmente los logs de envío</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-yellow-800 mb-2">📊 Rendimiento</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Prueba cada canal antes de activarlo</li>
              <li>• Monitorea las tasas de entrega</li>
              <li>• Configura múltiples canales como respaldo</li>
              <li>• Revisa las notificaciones fallidas</li>
            </ul>
          </div>
        </div>
        <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ Importante:</strong> Las notificaciones se enviarán automáticamente según los eventos
            configurados en tu estrategia de fidelización. Asegúrate de que la configuración esté correcta
            antes de activar los canales.
          </p>
        </div>
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <FontAwesomeIcon icon={faShield} className="w-5 h-5 mr-2 text-green-500" />
          Seguridad
        </h2>
        <p className="text-gray-600 mb-4">
          Configuraciones de seguridad adicionales estarán disponibles próximamente.
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium">Autenticación de dos factores</h3>
              <p className="text-sm text-gray-500">Añade una capa extra de seguridad</p>
            </div>
            <span className="text-sm text-gray-400">Próximamente</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium">Sesiones activas</h3>
              <p className="text-sm text-gray-500">Gestiona tus sesiones abiertas</p>
            </div>
            <span className="text-sm text-gray-400">Próximamente</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreferencesSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <FontAwesomeIcon icon={faPalette} className="w-5 h-5 mr-2 text-purple-500" />
          Preferencias
        </h2>
        <p className="text-gray-600 mb-4">
          Personaliza tu experiencia en la aplicación.
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium">Tema</h3>
              <p className="text-sm text-gray-500">Claro, oscuro o automático</p>
            </div>
            <span className="text-sm text-gray-400">Próximamente</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium">Idioma</h3>
              <p className="text-sm text-gray-500">Español, inglés, etc.</p>
            </div>
            <span className="text-sm text-gray-400">Próximamente</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-100 p-4">
        <div className="text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Configuración</h1>
          <p className="text-gray-600 mt-2">Gestiona tu cuenta y preferencias</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar de navegación */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-2">
              <h2 className="text-lg font-semibold mb-4 text-gray-900">Secciones</h2>
              {renderSectionButton('profile', faUser, 'Perfil', 'Información personal y cuenta', 'Activo')}
              {renderSectionButton('notifications', faBell, 'Notificaciones', 'Configurar canales de notificación')}
              {renderSectionButton('security', faShield, 'Seguridad', 'Configuraciones de seguridad')}
              {renderSectionButton('preferences', faPalette, 'Preferencias', 'Personalizar experiencia')}
            </div>
          </div>

          {/* Contenido principal */}
          <div className="lg:col-span-3">
            {activeSection === 'profile' && renderProfileSection()}
            {activeSection === 'notifications' && renderNotificationSection()}
            {activeSection === 'security' && renderSecuritySection()}
            {activeSection === 'preferences' && renderPreferencesSection()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
